// Utility functions for CardsView component

export const LONG_TITLE_MAX_LENGTH = 80;

export const STATUS_FILTER_EBAY_SYNC_FAILED = "ebay_sync_failed";
export const STATUS_FILTER_TITLE_LENGTH = "error:card_title_length";

export const getStatusFilterOptions = ({
  showTitleLengthFilter = false,
} = {}) => {
  const options = [
    { value: "", label: "All" },
    { value: "draft", label: "Draft" },
    { value: "ready", label: "Ready to List" },
    { value: "listing", label: "Listing" },
    { value: "listed", label: "Listed" },
    { value: STATUS_FILTER_EBAY_SYNC_FAILED, label: "eBay Sync Failed" },
  ];

  if (showTitleLengthFilter) {
    options.push({
      value: STATUS_FILTER_TITLE_LENGTH,
      label: "Title Too Long",
    });
  }

  return options;
};

export const cardHasLongTitle = (card) => card?.has_long_title === true;

export const getPageNumbers = (current, total) => {
  const boundaryCount = 2;
  const siblingCount = 1;

  if (total <= boundaryCount * 2 + siblingCount * 2 + 3) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const startPages = Array.from({ length: boundaryCount }, (_, i) => i + 1);
  const endPages = Array.from(
    { length: boundaryCount },
    (_, i) => total - boundaryCount + i + 1
  );

  const leftSibling = Math.max(current - siblingCount, boundaryCount + 1);
  const rightSibling = Math.min(current + siblingCount, total - boundaryCount);

  const showLeftEllipsis = leftSibling > boundaryCount + 2;
  const showRightEllipsis = rightSibling < total - boundaryCount - 1;

  const pages = [...startPages];

  if (showLeftEllipsis) {
    pages.push("start-ellipsis");
  } else if (leftSibling === boundaryCount + 2) {
    pages.push(boundaryCount + 1);
  }

  for (let i = leftSibling; i <= rightSibling; i++) {
    if (!pages.includes(i)) pages.push(i);
  }

  if (showRightEllipsis) {
    pages.push("end-ellipsis");
  } else if (rightSibling === total - boundaryCount - 1) {
    pages.push(total - boundaryCount);
  }

  pages.push(...endPages);

  return pages;
};

export const getLongTitleBannerMessage = (count) => {
  const n = count ?? 0;
  if (n <= 0) return "";
  const cardLabel = n === 1 ? "card has" : "cards have";
  return `${n} ${cardLabel} an eBay title longer than ${LONG_TITLE_MAX_LENGTH} characters. Shorten the title(s) before marking as ready to list or listing on eBay.`;
};

export const ensureHttps = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") && !url.includes("localhost")) {
    return url.replace("http://", "https://");
  }
  return url;
};

export const extractPageNumber = (url) => {
  if (!url) return null;
  const params = new URL(url).searchParams;
  return parseInt(params.get("page") || "1", 10);
};

export const normalizePageUrl = (url, apiBaseUrl) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return `${apiBaseUrl}${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
};

export const buildBaseListUrl = (apiBaseUrl, params) => {
  const {
    ordering,
    search,
    fixedFilters,
    columnFilters,
    cardsPerPage,
    page,
  } = params;
  const urlParams = new URLSearchParams();

  // Table view and inspector view share the same page size/page number so
  // switching between them stays on the same page
  urlParams.set("page_size", String(cardsPerPage));

  if (page && page > 1) urlParams.set("page", String(page));

  if (ordering && ordering.length > 0) urlParams.set("ordering", ordering);
  if (search && search.trim().length > 0)
    urlParams.set("search", search.trim());
  if (fixedFilters?.["batch__name"])
    urlParams.set("batch__name", fixedFilters["batch__name"]);

  // Add column filters
  if (columnFilters.status) urlParams.set("status", columnFilters.status);
  if (columnFilters.ocr_status)
    urlParams.set("ocr_status", columnFilters.ocr_status);

  return `${apiBaseUrl}/api/v1/cards/?${urlParams.toString()}`;
};

export const getColumnDisplayName = (columnKey) => {
  const displayNames = {
    images: "IMAGES",
    title: "TITLE",
    status: "STATUS",
    sale_price: "SALE PRICE",
    category: "CATEGORY",
    year: "YEAR",
    set: "SET",
    card_name: "CARD NAME",
    id: "ID",
    batch: "BATCH",
    ocr_status: "OCR STATUS",
    uploaded_at: "UPLOADED AT",
    description: "DESCRIPTION",
    condition: "CONDITION",
    producer: "PRODUCER",
    rarity: "RARITY",
    subset: "SUB SET",
    edition: "EDITION",
    variant: "VARIANT",
    language: "LANGUAGE",
    autograph: "AUTOGRAPH",
    card_number: "CARD NUMBER",
    market_value: "MARKET VALUE",
    grade: "GRADE",
    damage: "DAMAGE",
    graded: "GRADED",
    special_features: "SPECIAL FEATURES",
    selling_points: "SELLING POINTS",
    card_game_type: "CARD GAME TYPE",
    ebay_title: "TITLE",
    ebay_category: "CATEGORY",
    ebay_condition: "CONDITION",
    ebay_description: "DESCRIPTION",
    ebay_brand: "BRAND",
    ebay_card_name: "CARD NAME",
    ebay_set: "SET",
    ebay_subset: "SUBSET",
    ebay_variant: "VARIANT",
    ebay_year: "YEAR",
    ebay_rarity: "RARITY",
    ebay_country: "COUNTRY",
    ebay_language: "LANGUAGE",
    ebay_manufacturer: "MANUFACTURER",
    ebay_autographed: "AUTOGRAPHED",
    ebay_card_number: "CARD NUMBER",
    ebay_year_manufactured: "YEAR MANUFACTURED",
    ebay_graded: "GRADED",
    ebay_vintage: "VINTAGE",
    ebay_condition_description: "CONDITION DESCRIPTION",
    ebay_type: "TYPE",
    available_quantity: "AVAILABLE QUANTITY",
    suggested_price: "SUGGESTED PRICE",
  };
  return displayNames[columnKey] || columnKey.toUpperCase();
};

export const getOcrStatusColor = (status) => {
  switch (status) {
    case "completed":
      return "#10b981";
    case "processing":
      return "#f59e0b";
    case "failed":
      return "#ef4444";
    default:
      return "#6b7280";
  }
};

export const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

export const initializeImageLoading = (cards) => {
  const loadingState = {};
  const loadedState = {};

  cards.forEach((card) => {
    if (card.images?.front) {
      loadingState[`${card.id}-front`] = true;
      loadedState[`${card.id}-front`] = false;
    }
    if (card.images?.back) {
      loadingState[`${card.id}-back`] = true;
      loadedState[`${card.id}-back`] = false;
    }
  });

  return { loadingState, loadedState };
};

export const getFieldMapping = () => {
  return {
    // Direct CardMetadata fields
    title: "title",
    Title: "title",
    description: "description",
    Description: "description",
    category: "category",
    Category: "category",
    suggested_price: "ebay_listing.SuggestedPrice",
    SuggestedPrice: "ebay_listing.SuggestedPrice",
    condition: "condition",
    Condition: "condition",
    status: "status",
    images: "images",
    id: "id",
    batch: "batch",
    uploaded_at: "uploaded_at",

    // eBay listing fields
    ebay_title: "ebay_listing.Title",
    eBayTitle: "ebay_listing.Title",
    "eBay Title": "ebay_listing.Title",
    ebay_category: "category",
    eBayCategory: "category",
    ebay_condition: "condition",
    eBayCondition: "condition",
    ebay_description: "ebay_listing.Description",
    eBayDescription: "ebay_listing.Description",
    ebay_brand: "ebay_listing.ItemSpecifics.Brand",
    eBayBrand: "ebay_listing.ItemSpecifics.Brand",
    ebay_card_name: "ebay_listing.ItemSpecifics.Card Name",
    eBayCardName: "ebay_listing.ItemSpecifics.Card Name",
    ebay_set: "ebay_listing.ItemSpecifics.Set",
    eBaySet: "ebay_listing.ItemSpecifics.Set",
    ebay_subset: "ebay_listing.ItemSpecifics.SubSet",
    eBaySubSet: "ebay_listing.ItemSpecifics.SubSet",
    ebay_variant: "ebay_listing.ItemSpecifics.Variant",
    eBayVariant: "ebay_listing.ItemSpecifics.Variant",
    ebay_year: "ebay_listing.ItemSpecifics.Year",
    eBayYear: "ebay_listing.ItemSpecifics.Year",
    ebay_rarity: "ebay_listing.ItemSpecifics.Rarity",
    eBayRarity: "ebay_listing.ItemSpecifics.Rarity",
    ebay_country: "ebay_listing.ItemSpecifics.Country of Origin",
    eBayCountry: "ebay_listing.ItemSpecifics.Country of Origin",
    "EBAY COUNTRY": "ebay_listing.ItemSpecifics.Country of Origin",
    ebay_language: "ebay_listing.ItemSpecifics.Language",
    eBayLanguage: "ebay_listing.ItemSpecifics.Language",
    ebay_manufacturer: "ebay_listing.ItemSpecifics.Manufacturer",
    eBayManufacturer: "ebay_listing.ItemSpecifics.Manufacturer",
    ebay_autographed: "ebay_listing.ItemSpecifics.Autographed",
    eBayAutographed: "ebay_listing.ItemSpecifics.Autographed",
    ebay_card_number: "ebay_listing.ItemSpecifics.Card Number",
    eBayCardNumber: "ebay_listing.ItemSpecifics.Card Number",
    ebay_year_manufactured: "ebay_listing.ItemSpecifics.Year Manufactured",
    eBayYearManufactured: "ebay_listing.ItemSpecifics.Year Manufactured",
    ebay_graded: "ebay_listing.ItemSpecifics.Graded",
    eBayGraded: "ebay_listing.ItemSpecifics.Graded",
    ebay_vintage: "ebay_listing.ItemSpecifics.Vintage",
    eBayVintage: "ebay_listing.ItemSpecifics.Vintage",
    ebay_condition_description: "ebay_listing.Condition Description",
    eBayConditionDescription: "ebay_listing.Condition Description",
    ebay_type: "ebay_listing.ItemSpecifics.Type",
    eBayType: "ebay_listing.ItemSpecifics.Type",
    sale_price: "ebay_listing.SalePrice",
    available_quantity: "ebay_listing.AvailableQuantity",
  };
};
